/**
 * The websocket connection to a live game.
 *
 * Framework-free on purpose so it can be tested without React. It assumes the
 * connection *will* drop — phones lock, wifi hands over, tunnels blink — and
 * treats reconnecting as the normal case rather than an error path.
 */

import type { ClientMessage, ServerMessage } from '@shared/types/websocket';
import { WS_PATH } from '@shared/types/websocket';

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export function defaultWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const configured = import.meta.env.VITE_WS_URL as string | undefined;
  const host = configured || `${protocol}//${window.location.host}`;
  return `${host}${WS_PATH}`;
}

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 15_000;

/**
 * After this many failures in a row, suspect the door rather than the network.
 *
 * A browser cannot see *why* a handshake failed: a refused upgrade arrives as
 * close code 1006 with no status, exactly like a server that is restarting. So
 * a revoked device would otherwise back off politely and retry for ever against
 * a server that will never say yes. Three is past any ordinary restart.
 */
const FAILURES_BEFORE_SUSPECTING_THE_LOCK = 3;

export class GameSocket {
  private socket: WebSocket | null = null;
  private queue: ClientMessage[] = [];
  private attempt = 0;
  private retryTimer: number | null = null;
  private closedByUs = false;

  constructor(
    private readonly onMessage: (message: ServerMessage) => void,
    private readonly onStatus: (status: ConnectionStatus) => void,
    /** Sent on every (re)connect so the server can restore the seat. */
    private readonly hello: () => ClientMessage | null,
    private readonly url: string = defaultWsUrl(),
    /** Called when reconnecting keeps failing; see the constant above. */
    private readonly onPersistentFailure: () => void = () => {}
  ) {}

  connect(): void {
    this.closedByUs = false;
    this.open();

    // A phone that comes back from sleep or regains signal should not wait out
    // the backoff timer.
    window.addEventListener('online', this.wake);
    document.addEventListener('visibilitychange', this.wake);
  }

  close(): void {
    this.closedByUs = true;
    this.clearRetry();
    window.removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.wake);
    this.socket?.close();
    this.socket = null;
    this.onStatus('closed');
  }

  send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
      return;
    }
    // Hold it until the connection is back rather than dropping the action.
    this.queue.push(message);
  }

  private wake = () => {
    if (this.closedByUs) return;
    if (document.visibilityState === 'hidden') return;
    if (this.socket?.readyState === WebSocket.OPEN) return;
    this.clearRetry();
    this.attempt = 0;
    this.open();
  };

  private open(): void {
    this.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const socket = new WebSocket(this.url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.onStatus('open');

      const hello = this.hello();
      if (hello) socket.send(JSON.stringify(hello));

      const pending = this.queue;
      this.queue = [];
      for (const message of pending) socket.send(JSON.stringify(message));
    };

    socket.onmessage = (event) => {
      try {
        this.onMessage(JSON.parse(event.data as string) as ServerMessage);
      } catch {
        console.warn('unreadable message from the server');
      }
    };

    socket.onclose = () => {
      if (this.closedByUs) return;
      this.scheduleRetry();
    };

    socket.onerror = () => {
      socket.close();
    };
  }

  private scheduleRetry(): void {
    this.onStatus('reconnecting');
    this.clearRetry();

    // Exponential backoff with jitter, so a server restart does not get a
    // thundering herd from every phone at the table at once.
    const delay = Math.min(BASE_DELAY_MS * 2 ** this.attempt, MAX_DELAY_MS);
    const jittered = delay * (0.7 + Math.random() * 0.6);
    this.attempt += 1;

    if (this.attempt === FAILURES_BEFORE_SUSPECTING_THE_LOCK) this.onPersistentFailure();

    this.retryTimer = window.setTimeout(() => this.open(), jittered);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}
